// Team › Budget incidents (`/c/{slug}/agents/budget-incidents?de=&period=`; MONITORING-P2): the
// company's `budget_incident` rows — a warning threshold crossed, a cap hit, a reservation
// refused — per DE and period, each with its amount against the budget's, and whether it was
// resolved (an approval lifts a cap). Opened from the seat drawer's Finance links (`?de=` the
// seat's DE) and from Team › Alerts. Managers only (the API's rule). Budgets are core: no add-on
// gate. Calls on load: `monitoring.budgetIncidents` (the filter as `?de_membership_id`,
// `?period_key`, `?state`), plus the cached DE list for the DE filter.
import {
  Badge,
  EmptyState,
  InfoTip,
  LocationHeader,
  NativeSelect,
  NativeSelectOption,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  ToggleGroup,
  ToggleGroupItem,
  Toolbar,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { WalletIcon } from "lucide-react";
import { useId, useMemo, useState } from "react";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import type { CompanyAccess } from "../company";
import { formatDateTime, formatMoney } from "../format";
import { LoadError, Loading } from "../screen-parts";
import { type BudgetIncidentFilter, budgetIncidentsQuery } from "./agent-queries";
import { LIST_BODY } from "./agents-ux";
import { useDeNames } from "./runs-screen";

type Page = Awaited<ReturnType<Api["client"]["monitoring"]["budgetIncidents"]>>;
type Incident = Page["data"][number];

export const INCIDENT_KIND_LABELS: Record<Incident["kind"], string> = {
  threshold: "Warning crossed",
  cap: "Cap hit",
  reservation_failed: "Reservation refused",
};

const STATES = [
  ["all", "All"],
  ["open", "Open"],
  ["resolved", "Resolved"],
] as const;

const linkCls = "text-primary-text underline-offset-4 hover:underline";

const periodText = (i: Incident) => i.period_key ?? "Standing";

export function BudgetIncidentsScreen({
  api,
  company,
  de,
  period,
  onFilter,
}: {
  api: Api;
  company: CompanyAccess;
  de?: string | undefined;
  period?: string | undefined;
  onFilter: (next: { de?: string | undefined; period?: string | undefined }) => void;
}) {
  const [state, setState] = useState<BudgetIncidentFilter["state"]>("all");
  const q = useQuery(budgetIncidentsQuery(api, company.id, { de, period, state }));
  const names = useDeNames(api, company.id);
  const deId = useId();
  const periodId = useId();
  const rows = q.data?.data ?? [];
  // The periods the rows carry (newest first), for the period filter.
  const periods = useMemo(
    () =>
      [...new Set(rows.map((r) => r.period_key).filter((p): p is string => !!p))].sort().reverse(),
    [rows],
  );
  const nameOf = (i: Incident) =>
    i.de_name ?? (i.de_membership_id ? names.get(i.de_membership_id) : undefined) ?? "No DE";
  return (
    <div className={LIST_BODY} data-slot="budget-incidents">
      <LocationHeader
        icon={WalletIcon}
        title="Budget incidents"
        badge={
          <InfoTip label="About budget incidents">
            Each time a digital employee's spend crossed a budget warning, hit its cap, or had a run
            refused for want of budget. A cap stays hit until someone approves more.
          </InfoTip>
        }
        actions={
          <Link
            to="/c/$slug/agents/alerts"
            params={{ slug: company.slug }}
            search={keepFocus}
            className={`text-sm ${linkCls}`}
            data-slot="incidents-alerts-link"
          >
            Alerts
          </Link>
        }
      />
      <Toolbar bleed aria-label="Incident filters">
        <ToggleGroup
          variant="chip"
          value={[state]}
          onValueChange={(v: unknown[]) => {
            const next = v[0] as BudgetIncidentFilter["state"] | undefined;
            if (next) setState(next);
          }}
          aria-label="Show"
        >
          {STATES.map(([value, label]) => (
            <ToggleGroupItem key={value} value={value}>
              {label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        <label htmlFor={deId} className="sr-only">
          Digital employee
        </label>
        <NativeSelect
          id={deId}
          size="sm"
          value={de ?? ""}
          onChange={(e) => onFilter({ de: e.target.value || undefined, period })}
        >
          <NativeSelectOption value="">Every DE</NativeSelectOption>
          {[...names].map(([id, name]) => (
            <NativeSelectOption key={id} value={id}>
              {name}
            </NativeSelectOption>
          ))}
        </NativeSelect>
        <label htmlFor={periodId} className="sr-only">
          Period
        </label>
        <NativeSelect
          id={periodId}
          size="sm"
          value={period ?? ""}
          onChange={(e) => onFilter({ de, period: e.target.value || undefined })}
        >
          <NativeSelectOption value="">Every period</NativeSelectOption>
          {[...new Set([...(period ? [period] : []), ...periods])].map((p) => (
            <NativeSelectOption key={p} value={p}>
              {p}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </Toolbar>
      <div className="flex min-w-0 flex-col px-6 pt-3 max-md:px-4">
        {q.isPending ? (
          <Loading label="Loading budget incidents" rows={4} />
        ) : q.isError ? (
          <LoadError what="Budget incidents" error={q.error} />
        ) : !rows.length ? (
          <EmptyState
            variant="card"
            icon={WalletIcon}
            title="No budget incidents"
            description="Incidents show up here when a DE's spend crosses a warning or hits a cap."
          />
        ) : (
          <div className="overflow-x-auto">
            <Table data-slot="incidents-table">
              <TableCaption>Budget incidents, newest first.</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead scope="col">Incident</TableHead>
                  <TableHead scope="col">Digital employee</TableHead>
                  <TableHead scope="col">Period</TableHead>
                  <TableHead scope="col">Amount</TableHead>
                  <TableHead scope="col">Raised</TableHead>
                  <TableHead scope="col">State</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((i) => (
                  <TableRow key={i.id} data-incident={i.id} data-kind={i.kind}>
                    <TableHead scope="row" className="font-medium whitespace-normal">
                      <span className="inline-flex flex-wrap items-center gap-2">
                        {INCIDENT_KIND_LABELS[i.kind]}
                        {i.threshold !== null ? (
                          <Badge variant="warning">{Math.round(Number(i.threshold) * 100)} %</Badge>
                        ) : null}
                      </span>
                    </TableHead>
                    <TableCell>
                      {i.de_membership_id ? (
                        <Link
                          to="/c/$slug/agents/de/$id"
                          params={{ slug: company.slug, id: i.de_membership_id }}
                          search={keepFocus}
                          className={linkCls}
                        >
                          {nameOf(i)}
                        </Link>
                      ) : (
                        nameOf(i)
                      )}
                    </TableCell>
                    <TableCell className="tabular-nums">{periodText(i)}</TableCell>
                    <TableCell className="tabular-nums" data-slot="incident-amount">
                      {formatMoney(i.amount_minor, i.currency)}
                      <span className="text-muted-foreground">
                        {" "}
                        of {formatMoney(i.budget_amount_minor, i.currency)}
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground tabular-nums">
                      {formatDateTime(i.created_at)}
                    </TableCell>
                    <TableCell>
                      {i.resolved_at ? (
                        <Badge variant="neutral">Resolved {formatDateTime(i.resolved_at)}</Badge>
                      ) : (
                        <Badge variant="danger">Open</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
